import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  addMonths,
  addYears,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
  subYears,
} from 'date-fns';
import { resolveActiveDateFnsLocale } from '@/i18n/dateFnsLocale';
import { WEEK_STARTS_ON_MONDAY } from '@/utils/dateFormatting';
import { Icon } from '@/components/common/Icon';
import { cn } from '@/lib/utils';

interface DatePickerProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hasError?: boolean;
}

function parseValue(value: string): Date | null {
  if (value.trim().length === 0) {
    return null;
  }
  const parsed = parseISO(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function DatePicker({ id, value, onChange, placeholder, hasError = false }: DatePickerProps) {
  const { t } = useTranslation('common');
  const locale = resolveActiveDateFnsLocale();
  const selected = parseValue(value);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState<Date>(selected === null ? new Date() : selected);

  useEffect(
    function closeOnOutsideInteraction() {
      if (isOpen === false) {
        return;
      }
      function handlePointer(pointerEvent: MouseEvent) {
        if (
          containerRef.current !== null &&
          containerRef.current.contains(pointerEvent.target as Node) === false
        ) {
          setIsOpen(false);
        }
      }
      function handleKey(keyboardEvent: KeyboardEvent) {
        if (keyboardEvent.key === 'Escape') {
          setIsOpen(false);
        }
      }
      window.addEventListener('mousedown', handlePointer);
      window.addEventListener('keydown', handleKey);
      return function cleanup() {
        window.removeEventListener('mousedown', handlePointer);
        window.removeEventListener('keydown', handleKey);
      };
    },
    [isOpen]
  );

  function openCalendar() {
    setViewMonth(selected === null ? new Date() : selected);
    setIsOpen(true);
  }

  function selectDay(day: Date) {
    onChange(format(day, 'yyyy-MM-dd'));
    setIsOpen(false);
  }

  const weekStart = startOfWeek(new Date(), { ...WEEK_STARTS_ON_MONDAY, locale });
  const weekdayLabels = eachDayOfInterval({
    start: weekStart,
    end: endOfWeek(new Date(), { ...WEEK_STARTS_ON_MONDAY, locale }),
  }).map(function toLabel(day) {
    return format(day, 'EEEEEE', { locale });
  });

  const gridDays = eachDayOfInterval({
    start: startOfWeek(startOfMonth(viewMonth), { ...WEEK_STARTS_ON_MONDAY, locale }),
    end: endOfWeek(endOfMonth(viewMonth), { ...WEEK_STARTS_ON_MONDAY, locale }),
  });

  const today = new Date();

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        id={id}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={function toggle() {
          if (isOpen === true) {
            setIsOpen(false);
          } else {
            openCalendar();
          }
        }}
        className={cn(
          'w-full flex items-center justify-between gap-2 px-4 py-2.5 bg-zoooffwhite border rounded-xl text-left transition-all focus:outline-none focus:border-zooprimary focus:ring-2 focus:ring-zooprimary/40',
          hasError === true ? 'border-zooerrorborder ring-1 ring-zooerrorborder' : 'border-zooborder'
        )}
      >
        <span className={selected === null ? 'text-zoodisabledtext text-sm' : 'text-zooindigo text-sm'}>
          {selected === null
            ? placeholder === undefined
              ? ''
              : placeholder
            : format(selected, 'd MMMM yyyy', { locale })}
        </span>
        <Icon name="Calendar" className="text-zoomuted shrink-0" />
      </button>

      {isOpen === true ? (
        <div className="absolute z-30 mt-2 w-72 max-w-[calc(100vw-2rem)] bg-zoowhite border border-zooborder rounded-2xl shadow-xl p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1">
              <NavButton icon="ChevronLeft" label="«" onClick={() => setViewMonth(subYears(viewMonth, 1))} double />
              <NavButton icon="ChevronLeft" label="‹" onClick={() => setViewMonth(subMonths(viewMonth, 1))} />
            </div>
            <div className="text-sm font-bold text-zooindigo capitalize">
              {format(viewMonth, 'LLLL yyyy', { locale })}
            </div>
            <div className="flex items-center gap-1">
              <NavButton icon="ChevronRight" label="›" onClick={() => setViewMonth(addMonths(viewMonth, 1))} />
              <NavButton icon="ChevronRight" label="»" onClick={() => setViewMonth(addYears(viewMonth, 1))} double />
            </div>
          </div>

          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {weekdayLabels.map(function renderWeekday(label, index) {
              return (
                <div
                  key={index}
                  className="text-center text-[10px] font-bold uppercase text-zoomuted py-1"
                >
                  {label}
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-7 gap-0.5">
            {gridDays.map(function renderDay(day) {
              const isCurrentMonth = isSameMonth(day, viewMonth);
              const isSelected = selected !== null && isSameDay(day, selected);
              const isToday = isSameDay(day, today);
              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  onClick={function pick() {
                    selectDay(day);
                  }}
                  className={cn(
                    'h-9 w-full rounded-lg text-sm transition-colors',
                    isSelected === true
                      ? 'bg-zooprimary text-zoowhite font-bold'
                      : isCurrentMonth === true
                        ? 'text-zooindigo hover:bg-zoovpalelavender'
                        : 'text-zoodisabledtext hover:bg-zoovpalelavender',
                    isSelected === false && isToday === true ? 'ring-1 ring-zooprimary' : ''
                  )}
                >
                  {format(day, 'd', { locale })}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between mt-2 pt-2 border-t border-zooborder">
            <button
              type="button"
              onClick={function goToday() {
                selectDay(today);
              }}
              className="text-xs font-semibold text-zooprimary"
            >
              {t('date.today')}
            </button>
            {selected === null ? null : (
              <button
                type="button"
                onClick={function clear() {
                  onChange('');
                  setIsOpen(false);
                }}
                className="text-xs font-semibold text-zoomuted hover:text-zooerrortext"
              >
                {t('date.clear')}
              </button>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

interface NavButtonProps {
  icon: 'ChevronLeft' | 'ChevronRight';
  label: string;
  onClick: () => void;
  double?: boolean;
}

function NavButton({ icon, onClick, double = false }: NavButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-9 h-9 flex items-center justify-center rounded-lg text-zoomuted hover:bg-zoovpalelavender hover:text-zooprimary transition-colors"
    >
      {double === true ? (
        <span className="flex -space-x-1.5">
          <Icon name={icon} />
          <Icon name={icon} />
        </span>
      ) : (
        <Icon name={icon} />
      )}
    </button>
  );
}
